import React, { useEffect, useMemo, useState } from "react";
import dateFormat from "dateformat";
import useMeasure from "react-use-measure";
import { AssessmentReport, LearningRecordCourse, QuestionReport } from "src/types/coaching/learners";
import { Assessment, MultipleChoiceQuestion, Question, TextQuestion } from "src/types/coaching/courses";
import { QUESTION_TYPES } from "src/constants/coaching";

import { animated, Spring } from "@react-spring/web";
import Tooltip from "src/components/tooltip";

import _ from "lodash";
import cx from "classnames";
import styles from "./assessmentReport.module.scss"
import { FileService } from "src/utils/file.service";

interface AssessmentReportProps {
    course: LearningRecordCourse;
    assessment: Assessment;
    report: AssessmentReport;
    onEndAssessment: () => void;
    isReportLoaded?: boolean;
    currentAttempt?: number;
    className?: string;
    learnersView?: boolean;
  }
  
  const AssessmentReportView: React.FC<AssessmentReportProps> = ({ course, assessment, report, onEndAssessment, isReportLoaded, currentAttempt, className, learnersView }) => {
  
    const questions = useMemo(() => {
      return _.get(assessment, "questions") || [];
    }, [assessment]);
  
    const score = useMemo(() => {
      if (!report?.score && report?.score !== 0) {
        return "NA";
      }
      return ((report.score * 100).toFixed(0)) + "%";
    }, [report]);
  
    return (
      <div className={cx(styles.reportContainer, className)}>
        {!isReportLoaded && <AssessmentReportSkeleton />}
        {isReportLoaded && (
          <>
            <div className={styles.header}>
              <h3 className={styles.title}>attempt {currentAttempt} breakdown</h3>
              <p className={styles.score}>here&apos;s your overall score for this assessment <span>{score}</span></p>
              <p className={styles.details}>   completed on {report?.completedDate
                ? dateFormat(new Date(report.completedDate), "dd mmm yyyy, HH:MM:ss")
                : "NA"}
                <div className={styles.divider} />
                <div className={styles.metadata}>
                  duration {FileService.formatTimeDuration(report?.duration)}
                </div>
              </p>
              {!learnersView && <div className={styles.closeIcon} onClick={onEndAssessment}>
                <i className="paperflite-close" />
              </div>}
            </div>
            <div className={styles.panelContainer}>
              <div className={styles.questions}>
                {_.map(questions, (question, index) => (
                  <QuestionAccordianPanel key={index}
                    index={index}
                    course={course}
                    assessment={assessment}
                    question={question}
                    report={report}
                  />
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    );
  }
  
  interface QuestionAccordianPanelProps {
    index: number;
    course: LearningRecordCourse;
    assessment: Assessment;
    question: Question;
    report: AssessmentReport;
  }
  
  const QuestionAccordianPanel: React.FC<QuestionAccordianPanelProps> = ({ index, course, assessment, question,report }) => {
    const [expanded, setExpanded] = useState(false);
  
    const [dimensions, setDimensions] = React.useState<{ height: number | string }>({
      height: 0,
    });
  
    const questionType = useMemo(() => {
      const type = _.get(question, "type");
      return QUESTION_TYPES.find((t) => t.id === type)?.value || "NA";
    }, [question]);  

    const questionReport = useMemo(() => {
        return report?.questions?.[index] || null;
      }, [report, index]);
  
    const score = useMemo(() => {
      return ((questionReport?.score || 0) * 100).toFixed(0);
    }, [questionReport]);
  
    const answerType = useMemo(() => {
      return _.get(question, "type");
    }, [question]);
  
    const [expandedPanelRef, expandedPanelBounds] = useMeasure();
    const [collapsedPanelRef, collapsedPanelBounds] = useMeasure();
  
    useEffect(() => {
      let height: any = 0;
      if (expanded) {
        height = (expandedPanelBounds.height || 0) + 40;
      } else {
        height = (collapsedPanelBounds.height || 0) + 40;
      }
  
      setDimensions({
        height: height
      });
  
    }, [expandedPanelBounds, collapsedPanelBounds, expanded]);
  
  
    return (
      <Spring to={{ height: dimensions.height }} config={{
        friction: 14,
        tension: 120,
        bounce: 1,
      }}>
        {props => (
          <animated.div style={{ ...props }}
            className={cx(styles.questionAccordianPanel)}
          >
            <div ref={collapsedPanelRef} className={cx(styles.body, styles.collapsed, {
              [styles.show]: !expanded
            })} onClick={() => setExpanded(true)}>
              <div className={styles.index}>{index + 1}</div>
              <div className={styles.question}>
                <div className={styles.name}>
                  {question.name}
                </div>
                <div className={styles.type}>
                  Question Type: {questionType}
                </div>
              </div>
              <div className={styles.actions}>
                <button className={cx(styles.action, styles.answer, {
                  [styles.correct]: questionReport?.correct,
                })}>
                  <i className={questionReport?.correct ? "paperflite-tick-unselected" : "paperflite-close"}></i>
                </button>
                <button className={cx(styles.action, styles.dropdown, styles.expand)}>
                  <i className="paperflite-dropdown"></i>
                </button>
              </div>
            </div>
            <div ref={expandedPanelRef} className={cx(styles.body, styles.expanded, {
              [styles.show]: expanded
            })}>
              <div className={styles.header} onClick={() => setExpanded(false)}>
                <div className={styles.index}>Question {index + 1}</div>
                {answerType === "text" &&
                  <div className={styles.score}>score <b>{score}%</b></div>
                }
                <div className={styles.actions}>
                  <button className={cx(styles.action, styles.collapse)}>
                    <i className="paperflite-dropdown"></i>
                  </button>
                </div>
              </div>
              <QuestionReportContainer
                key={index}
                course={course}
                assessment={assessment}
                question={question}
                report={questionReport}
              />
            </div>
          </animated.div>
        )}
      </Spring>
    );
  }
  
  interface QuestionReportContainerProps {
    course: LearningRecordCourse;
    assessment: Assessment;
    question?: Question;
    report?: QuestionReport;
  }
  
  const QuestionReportContainer: React.FC<QuestionReportContainerProps> = ({
    course,
    assessment,
    question,
    report,
  }) => {
    const name = useMemo(() => {
      return _.get(question, "name");
    }, [question]);
  
    const answerType = useMemo(() => {
      return _.get(question, "type");
    }, [question]);
  
    return (
      <div className={cx(styles.questionContainer, styles.questionReportContainer, styles[answerType || ""])}>
        <div className={styles.nameField}>{name}</div>
  
        <div className={styles.answerField}>
          {answerType === "multiple_choice" && (
            <MultipleChoiceQuestionReportContainer
              question={question as MultipleChoiceQuestion}
              questionReport={report}
            />
          )}
  
          {answerType === "true_false" && (
            <MultipleChoiceQuestionReportContainer
              question={question as MultipleChoiceQuestion}
              questionReport={report}
            />
          )}
  
          {answerType === "text" && (
            <TextReportAnswer
              question={question as TextQuestion}
              questionReport={report}
            />
          )}
        </div>
      </div>
    );
  };
  
  interface MultipleChoiceQuestionReportContainerProps {
    question: MultipleChoiceQuestion;
    questionReport?: QuestionReport;
  }
  
  const MultipleChoiceQuestionReportContainer: React.FC<MultipleChoiceQuestionReportContainerProps> = ({ question, questionReport }) => {
  
    const options = useMemo(() => {
      return _.get(question, "options") || [];
    }, [question]);
  
  
    return (
      <div className={cx(styles.multipleChoiceQuestionContainer, styles.report)}>
        <div className={styles.optionsContainer}>
          {_.map(options, (option, index) => {
            const correct = option.uuid === questionReport?.correctAnswer;
            const selected = option.uuid === questionReport?.answer;
  
            let icon = "paperflite-radio-button-unselected";
            if (selected) {
              icon = correct ? 'paperflite-tick-unselected' : 'paperflite-close';
            } else if (correct) {
              icon = 'paperflite-tick-unselected';
            }
  
            return (
              <div
                className={cx(styles.optionContainer, {
                  [styles.selected]: selected,
                  [styles.correct]: correct,
                })}
              >
                <Tooltip disabled={!(selected || correct)} content={correct ? "Correct Answer" : "Your Answer"} placement="top">
                  <div className={cx(styles.radio, {
                    [styles.selectedRadio]: selected,
                  })}
                  >
                    <i className={icon}></i>
                  </div>
                </Tooltip>
                <div className={styles.optionText}>
                  <span>{option.name}</span>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    );
  };
  interface TextAnswerReportProps {
    question: Question;
    questionReport?: QuestionReport;
  }
  
  const TextReportAnswer: React.FC<TextAnswerReportProps> = ({
    question,
    questionReport
  }) => {
  
    return (
      <div className={cx(styles.textAnswerReport, styles.report)}>
        <div className={styles.answer}>
          <div className={styles.label}>Your Answer:</div>
          <div className={styles.value}>{questionReport?.answer || ""}</div>
        </div>
  
        <div className={styles.feedback}>
          <div className={styles.label}>Feedback:</div>
          <div className={styles.value}>{questionReport?.feedback || ""}</div>
        </div>
      </div>
    );
  };

  export default AssessmentReportView;


  const AssessmentReportSkeleton = () => {
    return (
      <div className={styles.wrapper}>
        <div className={styles.line} />
        <div className={styles.text} />
        <div className={styles.card} />
        <div className={styles.card} />
        <div className={styles.card} />
      </div>
    );
  };